import React, { useRef, useState, useEffect } from 'react';
import Button from '@mui/material-next/Button';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import PropTypes from "prop-types";
import { Autocomplete, Box, IconButton, TextField } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from "react-i18next";
import Stack from '@mui/material/Stack';
import * as Yup from "yup";
import { Formik, Form } from 'formik'
import { get } from "lodash";
import CustomChip from '../../../widget/CustomChip';
import { ExpandMoreRounded, HighlightOffRounded } from '@material-ui/icons';
import BlurryDialog from '../../../widget/BlurryDialog';
import ApiClient from "../../../api/ApiClient";
import PromiseBuilder from "../../../utils/PromiseUtils";
import { usersDtoToJ } from '../../../utils/CampaignMapper';
import { useNotifications } from "../../../hooks/notification/notificationHook";

const initialForm = {
  recruiters: []
}

const KanbanAccessDialog = (props) => {
  const { t } = useTranslation();
  const { addErrorNotification } = useNotifications();

  const [recruiters, setRecruiters] = useState([]);
  const [form, setForm] = useState(initialForm);

  const descriptionElementRef = useRef(null);

  const CampaignSchema = Yup.object().shape({
    recruiters: Yup.array()
      .of(
        Yup.object().shape({
          id: Yup.string(),
          label: Yup.string()
        })
      ).test({
        message: t('validation.required'),
        test: arr => arr.length > 0,
      })
  });

  const fetchRecruiterData = () => {
    props.setIsLoading(true);
    new PromiseBuilder()
      .then(async () => ApiClient.get("/api/v1/user/recruiter/" + props.groupName))
      .then(async (res) => {
        props.setIsLoading(false);
        const recruitersJ = usersDtoToJ(res.data.users);
        setRecruiters(recruitersJ);
        setForm({ recruiters: props.recruiters.map((e, i) => {
          return recruitersJ.find(element => element.id === e.id);
        }) });
      })
      .catch(async (e) => {
        props.setIsLoading(false);
        addErrorNotification(t("toast.fetchRecruitersFailed"), t("toast.error"));
        console.log("ERROR", e);
      })
      .run();
  }

  useEffect(() => {
    fetchRecruiterData();
  }, []);

  useEffect(() => {
    if (props.open) {
      const { current: descriptionElement } = descriptionElementRef;
      if (descriptionElement !== null) {
        descriptionElement.focus();
      }
      if (recruiters.length > 0) {
        setForm({ recruiters: props.recruiters.map((e, i) => {
          return recruiters.find(element => element.id === e.id);
        }) });
      }
    }
  }, [props.open]);

  return (
    <Formik
      enableReinitialize={true}
      initialValues={form}
      validationSchema={CampaignSchema}
      onSubmit={(values, {resetForm}) => {
        props.handleSave(values.recruiters, resetForm)
      }}
    >
      {({ handleSubmit, handleChange, values, setFieldValue, errors, isValid, dirty, resetForm }) => (
        <Form>
          <BlurryDialog
            open={props.open}
            onClose={() => {
              props.handleClose(resetForm)
            }}
          >
            <DialogTitle
              id="scroll-dialog-title"
              className="color color-medium-purple">
              <b>
                {t("campaign.manageCampaign")}
              </b>
            </DialogTitle>
            <IconButton
              aria-label="close"
              onClick={() => {
                props.handleClose(resetForm)
              }}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
              }}
            >
              <CloseIcon />
            </IconButton>
            <DialogContent>
              <DialogContentText
                id="scroll-dialog-description"
                ref={descriptionElementRef}
                tabIndex={-1}
              >
                <Stack spacing={3}>
                  <Autocomplete
                    multiple
                    id="dialog-campaign-recruiters"
                    value={get(values, "recruiters")}
                    name={'recruiters'}
                    onChange={(event, newValue) => {
                      setFieldValue('recruiters', newValue)
                    }}
                    options={recruiters}
                    getOptionLabel={(option) => option.label}
                    filterSelectedOptions
                    style={{ marginBottom: '15px' }}
                    renderOption={(props, option) => (
                      <Box component="li" sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...props}>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <div style={{ display: 'flex', flexDirection: 'row', fontWeight: '800' }}>
                            {option.name} {option.surname}
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'row' }}>
                            {option.label}
                          </div>
                        </div>

                      </Box>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        size="small"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "10px"
                          }
                        }}
                        label={t("campaign.addRecruiters")}
                        placeholder={t("campaign.addRecruiters")}
                      />
                    )}
                    renderTags={(values, getTagProps) =>
                      values.map((value, index) => (
                        <CustomChip
                          id={index} key={index}
                          className="chip bg-dark-gray"
                          size="small"
                          color="#575760"
                          deleteIcon={
                            <HighlightOffRounded />
                          }
                          label={value.label} {...getTagProps({ index })}
                        />
                      ))
                    }
                    popupIcon={<ExpandMoreRounded />}
                  />
                </Stack>

              </DialogContentText>
            </DialogContent>
            <DialogContent>
              <DialogActions>
                <Button
                  variant="filled"
                  disableElevation
                  disabled={!isValid || !dirty}
                  className="bg bg-medium-purple"
                  type="submit"
                  size="small"
                  style={{ borderRadius: '10px' }}
                  onClick={handleSubmit}
                >
                  {t("save")}
                </Button>
              </DialogActions>
            </DialogContent>
          </BlurryDialog>
        </Form>
      )}
    </Formik>
  );
}

KanbanAccessDialog.propTypes = {
  groupName: PropTypes.string,
  recruiters: PropTypes.array,
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  handleSave: PropTypes.func,
  isLoading: PropTypes.bool,
  setIsLoading: PropTypes.func
};

export default KanbanAccessDialog;